<script lang="ts" setup>
import { ref, watch, inject, onMounted, reactive } from 'vue';
import { Vector2, Vector3 } from '@babylonjs/core/Maths/math.vector';
import CollapseFieldset from '@/components/CollapseFieldset.vue';
import LightPlacementBackground from '@/assets/images/globe_circle.svg?url';

import type { Ref } from 'vue';
import type { IDirectionalLightWithShadow, Viewer, Extension, Nullable } from '@immersive-tech/viewer-engine';
import type { IManifest } from '@/types/IManifest';
import type { Lights } from '@/viewerExtensions';

const viewer = inject('viewer') as Ref<Nullable<Viewer>>;
const viewerLights = ref<IDirectionalLightWithShadow[]>([]);
const manifest = inject('manifest') as Ref<IManifest>;
const boundingElement = ref<Nullable<HTMLImageElement>>(null);
const pointerElement = ref<Nullable<HTMLDivElement>>(null);
const point2D = ref(new Vector2());
const point3D = reactive(new Vector3());
const cssPoint = reactive(new Vector2());
const boundingElementWidth = ref(0);
const boundingElementHeight = ref(0);
const lightsExtension = viewer.value?.extensions.get('Lights') as Extension<Lights>;

const point2DWatcher = (newValue: Vector2) => {
  if (newValue.length() > 1) {
    point2D.value.normalize();

    return;
  }

  const newCSSPoint = new Vector2()
    .copyFrom(newValue)
    .divide(new Vector2(2, 2))
    .add(new Vector2(0.5, 0.5))
    .multiply(new Vector2(boundingElementHeight.value, boundingElementHeight.value));

  cssPoint.copyFrom(newCSSPoint);
  point3D.set(newValue.x, Math.sqrt(1 - newValue.x ** 2 - newValue.y ** 2) || 0, newValue.y);
};

const point3DWatcher = (newValue: Vector3) => {
  if (!viewer.value) return;

  manifest.value.lights[0].position = { x: newValue.x, y: newValue.y, z: newValue.z };
  lightsExtension.lights[0].position.copyFrom(newValue);
  lightsExtension.adjustLightToBoundingSphere(lightsExtension.shadowLight as IDirectionalLightWithShadow);
};

const cssPointWatcher = (newValue: Vector2) => {
  pointerElement.value!.style.left = newValue.x + 'px';
  pointerElement.value!.style.top = newValue.y + 'px';
};

const windowPointerMoveHandler = (event: PointerEvent) => {
  const { x, y } = boundingElement.value!.getBoundingClientRect();

  const tempPoint = new Vector2()
    .set(event.clientX - x, event.clientY - y)
    .divide(new Vector2(boundingElementHeight.value, boundingElementWidth.value))
    .subtract(new Vector2(0.5, 0.5))
    .multiply(new Vector2(2, 2));

  point2D.value.copyFrom(tempPoint);
};

const windowPointerUpHandler = () => {
  pointerElement.value!.classList.remove('cursor-grabbing');
  window.removeEventListener('pointermove', windowPointerMoveHandler);
};

const pointerElementPointerDownHandler = (event: PointerEvent) => {
  event.preventDefault();

  pointerElement.value!.classList.add('cursor-grabbing');

  window.addEventListener('pointerup', windowPointerUpHandler, { once: true });
  window.addEventListener('pointermove', windowPointerMoveHandler);
};

const boundingElementWatcher = (newValue: Nullable<HTMLImageElement>) => {
  if (!newValue) return;

  newValue.addEventListener('load', () => {
    const lightPosition = manifest.value.lights[0].position;
    const boundingElementBoundingRect = boundingElement.value?.getBoundingClientRect();

    if (!boundingElementBoundingRect) return;

    boundingElementHeight.value = boundingElementBoundingRect.height;
    boundingElementWidth.value = boundingElementBoundingRect.width;
    pointerElement.value?.classList.remove('hidden');
    pointerElement.value?.classList.add('block');

    point2D.value = new Vector2(lightPosition.x, lightPosition.z);
  });
};

const pointerElementWatcher = (newValue: Nullable<HTMLDivElement>) => {
  if (!newValue) return;

  newValue.addEventListener('pointerdown', pointerElementPointerDownHandler);
};

const manifestWatcher = (newValue: IManifest, oldValue: IManifest) => {
  if (JSON.stringify(newValue) === JSON.stringify(oldValue)) return;

  point2D.value = new Vector2(newValue.lights[0].position.x, newValue.lights[0].position.z);
};

const lightIntensityWatcher = (newValue: number, oldValue: number) => {
  if (newValue === oldValue) return;
  if (!viewer.value) return;

  lightsExtension.lights[0].intensity = Number(newValue) * 2.5;

  viewer.value.renderNextFrame += 1;
};

const mountHandler = () => {
  if (!viewer.value) return;

  viewerLights.value = lightsExtension.lights;
};

watch(manifest, manifestWatcher);
watch(pointerElement, pointerElementWatcher);
watch(boundingElement, boundingElementWatcher);
watch(point2D, point2DWatcher, { deep: true });
watch(cssPoint, cssPointWatcher, { deep: true });
watch(point3D, point3DWatcher, { deep: true });
watch((): number => manifest.value.lights[0].lightIntensity, lightIntensityWatcher);
onMounted(mountHandler);
</script>

<template>
  <CollapseFieldset :enable-collapse="false">
    <template #legend>
      Light Placement Configuration
    </template>
    <div id="light-config-wrapper" class="w-full relative select-none mt-auto mx-0 mb-0 block self-center">
      <div id="light-config-slider" ref="pointerElement" class="hidden bg-yellow-500 border-2 border-gray-50 -translate-x-1/2 -translate-y-1/2 w-[15px] h-[15px] absolute rounded-full cursor-grab select-none z-10" draggable="true" />
      <img id="boundingElement" ref="boundingElement" class="bg-color-black/15 w-full rounded-full select-none" :src="LightPlacementBackground" draggable="false">
    </div>
    <div class="flex flex-col text-xs my-2">
      <p class="mb-1">
        Light Placement Coordinates
      </p>
      <div class="pl-2 flex flex-row font-mono leading-none mb-1 items-center text-sm">
        <span class="font-mono">x:&nbsp;</span>
        <input
          v-model="point2D.x"
          type="number"
          class="border w-full"
          min="-1"
          max="1"
          step="0.005"
        >
      </div>
      <div class="pl-2 flex flex-row font-mono leading-none items-center text-sm">
        <span class="font-mono">y:&nbsp;</span>
        <input
          v-model="point2D.y"
          type="number"
          class="border w-full"
          min="-1"
          max="1"
          step="0.005"
        >
      </div>
    </div>
  </CollapseFieldset>
</template>
